import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '../css/DynamicComponentLoader.css';
import '../css/login.css';

import Dashboard from './dashboard';
import Income from './income';
import Expenses from './expenses';
import Accounts from './accounts';
import Credits from './credits';
import Transactions from './transactions';
import Archive from './archive';
import Analysis from './analysis';
import Onboarding from './onboarding';
import Profile from './profile';

const DynamicComponentLoader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [password3, setPassword3] = useState('');
  const [email, setEmail] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [loggedInUsername, setLoggedInUsername] = useState('');
  const [isForgotPassword, setIsForgetPassword] = useState(false);
  const [isForgotPasswordSent, setIsForgetPasswordSent] = useState(false);
  const [isForgetPasswordFlow, setIsForgetPasswordFlow] = useState(false);
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [token, setToken] = useState(false);
  const [passwordNotMatch, setPasswordNotMatch] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [setupChecked, setSetupChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingMonth, setOnboardingMonth] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteStatusMessage, setInviteStatusMessage] = useState('');
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');

  const checkSetupStatus = async (tokenValue, usernameValue) => {
    if (!tokenValue || !usernameValue) {
      setNeedsOnboarding(false);
      setSetupChecked(true);
      return false;
    }

    try {
      const response = await fetch('/api/accounting/setup/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenValue}`
        },
        body: JSON.stringify({ user: usernameValue })
      });

      if (!response.ok) {
        throw new Error('Failed to load setup status');
      }

      const data = await response.json();
      const shouldOnboard = Boolean(data?.needs_onboarding);
      setNeedsOnboarding(shouldOnboard);
      setOnboardingMonth(data?.onboarding_month || '');
      setSetupChecked(true);
      return shouldOnboard;
    } catch (error) {
      setNeedsOnboarding(false);
      setOnboardingMonth('');
      setSetupChecked(true);
      return false;
    }
  };

  const handleOnboardingComplete = (monthKey) => {
    setNeedsOnboarding(false);
    setOnboardingMonth(monthKey || onboardingMonth || '');
    setSetupChecked(true);
    navigate('/dashboard', { replace: true });
  };

  const handleComponentChange = (path) => {
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
    navigate(path);
  };

  const toggleLoginForm = () => {
    setShowLoginForm(!showLoginForm);
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isLogin ? "/api/auth/login" : "/api/auth/register";
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
          email,
          invite_token: isLogin ? undefined : inviteToken
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        if (data.access_token) {
          localStorage.setItem('isLoggedIn', true);
          localStorage.setItem('accessToken', data.access_token);
          localStorage.setItem('loggedInUsername', data.username);

          setAccessToken(data.access_token);
          setLoggedInUsername(data.username);
          setIsLoggedIn(true);
          setShowLoginForm(false);
          setInviteStatusMessage('');
          setInviteErrorMessage('');

          const shouldOnboard = await checkSetupStatus(data.access_token, data.username);
          handleComponentChange(shouldOnboard ? '/onboarding' : '/dashboard');
        } else if (response.status === 201) {
          setIsLogin(true);
          setIsLoggedIn(false);
          setShowLoginForm(true);
        }
      } else {
        setInviteErrorMessage(data?.error || 'Login or registration failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setInviteErrorMessage('Unable to contact server');
    }
    setUsername('');
    setPassword('');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setSidebarOpen(false);
    setInviteStatusMessage('');
    setInviteErrorMessage('');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('loggedInUsername');
    setAccessToken(null);
    setLoggedInUsername('');
    setShowLoginForm(true);
    navigate('/login', { replace: true });
    setSetupChecked(false);
    setNeedsOnboarding(false);
    setOnboardingMonth('');
  };

  const checkInviteIsValid = async (inviteValue) => {
    if (!inviteValue) {
      return;
    }

    try {
      const response = await fetch('/api/auth/check_invite_valid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: inviteValue })
      });

      if (response.ok) {
        setInviteStatusMessage('Invite link detected. Complete registration within 5 minutes.');
        setInviteErrorMessage('');
      } else {
        const data = await response.json().catch(() => ({}));
        setInviteStatusMessage('');
        setInviteErrorMessage(data?.error || 'Invite link is invalid or expired');
      }
    } catch (error) {
      setInviteStatusMessage('');
      setInviteErrorMessage('Unable to validate invite link');
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    try {
      fetch("/api/auth/reset_password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      setEmail('');
      setIsForgetPassword(false);
      setIsForgetPasswordSent(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePasswordReset = async (passwordValue, confirm) => {
    if (passwordValue !== confirm) {
      setPasswordNotMatch(true);
      return;
    }

    try {
      const response = await fetch("/api/auth/reset_with_token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password: passwordValue }),
      });
      if (response.ok) {
        setTokenIsValid(false);
        setIsForgetPassword(false);
        setIsForgetPasswordFlow(false);
        setIsLogin(true);

        const newUrl = window.location.protocol + "//" + window.location.host;
        window.history.replaceState({ path: newUrl }, '', newUrl);
      } else {
        console.error('Password Reset Failure');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const checkTokenIsValid = async (tokenValue) => {
    try {
      const response = await fetch("/api/auth/check_token_valid", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: tokenValue }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setTokenIsValid(true);
          setToken(tokenValue);
        } else {
          setTokenIsValid(false);
        }
      } else {
        console.error('Token validation failed');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  function extractTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('passwordreset');
  }

  function extractInviteFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('invite');
  }

  useEffect(() => {
    const tokenFromUrl = extractTokenFromUrl();
    const inviteFromUrl = extractInviteFromUrl();

    if (inviteFromUrl) {
      setIsLogin(false);
      setShowLoginForm(true);
      setInviteToken(inviteFromUrl);
      checkInviteIsValid(inviteFromUrl);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    }

    if (tokenFromUrl != null) {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('loggedInUsername');
      setAccessToken(null);
      setLoggedInUsername('');
      setIsForgetPasswordFlow(true);
      checkTokenIsValid(tokenFromUrl);
    }

    const loggedInStatus = localStorage.getItem('isLoggedIn');
    if (loggedInStatus === 'true') {
      setIsLoggedIn(true);
      const savedAccessToken = localStorage.getItem('accessToken');
      const savedUsername = localStorage.getItem('loggedInUsername');
      if (savedAccessToken && savedUsername) {
        setShowLoginForm(false);
        setAccessToken(savedAccessToken);
        setLoggedInUsername(savedUsername);
      }
    } else {
      setSidebarOpen(false);
      setShowLoginForm(true);
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (!authChecked) {
      return;
    }
    if (!isLoggedIn) {
      setShowLoginForm(true);
      setSetupChecked(false);
      setNeedsOnboarding(false);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
      return;
    }

    if (!setupChecked) {
      return;
    }

    if (needsOnboarding && location.pathname !== '/onboarding') {
      navigate('/onboarding', { replace: true });
      return;
    }

    if (!needsOnboarding && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/onboarding')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authChecked, isLoggedIn, setupChecked, needsOnboarding, location.pathname, navigate]);

  useEffect(() => {
    if (!authChecked || !isLoggedIn) {
      return;
    }

    if (!accessToken || !loggedInUsername) {
      return;
    }

    checkSetupStatus(accessToken, loggedInUsername);
  }, [authChecked, isLoggedIn, accessToken, loggedInUsername]);

  const handlePasswordResetSent = () => {
    setIsForgetPasswordSent(false);
    setIsLogin(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const modalOpen = Boolean(
      showLoginForm ||
      isForgotPassword ||
      isForgetPasswordFlow ||
      isForgotPasswordSent ||
      passwordNotMatch
    );
    document.body.classList.toggle('modal-open', modalOpen);
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [
    showLoginForm,
    isForgotPassword,
    isForgetPasswordFlow,
    isForgotPasswordSent,
    passwordNotMatch
  ]);

  return (
    <div className="page-container">
      <header className="header">
        <div className="header-shell">
          <div className="title-container">
            <h1>Manderley Accounting</h1>
          </div>
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            ☰
          </button>
          <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={closeSidebar}></div>
          <nav className={`header-nav ${sidebarOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/dashboard')} disabled={!isLoggedIn}>
                <i className="fas fa-chart-line"></i> Dashboard
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/income')} disabled={!isLoggedIn}>
                <i className="fas fa-wallet"></i> Income
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/expenses')} disabled={!isLoggedIn}>
                <i className="fas fa-file-invoice-dollar"></i> Expenses
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/accounts')} disabled={!isLoggedIn}>
                <i className="fas fa-piggy-bank"></i> Account Forecasts
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/credits')} disabled={!isLoggedIn}>
                <i className="fas fa-credit-card"></i> Credit
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/transactions')} disabled={!isLoggedIn}>
                <i className="fas fa-receipt"></i> Transactions
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/archive')} disabled={!isLoggedIn}>
                <i className="fas fa-archive"></i> Archive
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/analysis')} disabled={!isLoggedIn}>
                <i className="fas fa-chart-bar"></i> Analysis
              </button>
            </div>
            <div>
              <button className="nav-button" onClick={() => handleComponentChange('/profile')} disabled={!isLoggedIn}>
                <i className="fas fa-user"></i> Profile
              </button>
            </div>
            {isLoggedIn ? (
              <div>
                <button className="logout-button" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i> Logout {loggedInUsername}
                </button>
              </div>
            ) : (
              <button className="auth-button" onClick={toggleLoginForm}>
                <i className="fas fa-sign-in-alt"></i> {isLogin ? 'Login' : 'Register'}
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="app-main">
        <Routes>
          <Route path="/" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />} />
          <Route path="/login" element={isLoggedIn ? <Navigate to="/dashboard" replace /> : null} />
          <Route
            path="/dashboard"
            element={authChecked && isLoggedIn ? <Dashboard onboardingMonth={onboardingMonth} /> : null}
          />
          <Route path="/income" element={authChecked && isLoggedIn ? <Income /> : null} />
          <Route path="/expenses" element={authChecked && isLoggedIn ? <Expenses /> : null} />
          <Route path="/accounts" element={authChecked && isLoggedIn ? <Accounts onboardingMonth={onboardingMonth} /> : null} />
          <Route path="/credits" element={authChecked && isLoggedIn ? <Credits /> : null} />
          <Route path="/transactions" element={authChecked && isLoggedIn ? <Transactions /> : null} />
          <Route path="/archive" element={authChecked && isLoggedIn ? <Archive /> : null} />
          <Route path="/analysis" element={authChecked && isLoggedIn ? <Analysis /> : null} />
          <Route path="/profile" element={authChecked && isLoggedIn ? <Profile /> : null} />
          <Route path="/onboarding" element={authChecked && isLoggedIn ? <Onboarding onComplete={handleOnboardingComplete} /> : null} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {showLoginForm && !isLoggedIn && !isForgotPassword && !isForgetPasswordFlow && !isForgotPasswordSent && (
        <article className="login-form">
          <h2>{isLogin ? 'Login' : 'Register'}</h2>
          <form onSubmit={handleSubmit}>
            <label>
              <input placeholder="Username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
            </label>
            <label>
              <input placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            {!isLogin && (
              <label>
                <input placeholder="Email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
            )}
            <div className="button-group">
              <button className="login-button" type="submit">{isLogin ? 'Login' : 'Register'}</button>
              <button className="login-button" type="button" onClick={toggleMode}>
                {isLogin ? 'Switch to Register' : 'Switch to Login'}
              </button>
            </div>
            {inviteStatusMessage && (
              <p>{inviteStatusMessage}</p>
            )}
            {inviteErrorMessage && (
              <p>{inviteErrorMessage}</p>
            )}
            <div className='button-group'>
              <button className="login-button" onClick={() => setIsForgetPassword(true)}>Forgot Password?</button>
            </div>
          </form>
        </article>
      )}

      {isForgotPassword && !isForgetPasswordFlow && (
        <article className="login-form">
          <form onSubmit={handleForgotPassword}>
            <h2>Forget Password?</h2>
            <label>
              <input placeholder="Email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
              <button className="login-button" type='submit'>Request Password Reset Link</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center', marginTop: '10px' }}>
              <button className="login-button" type="button" onClick={() => setIsForgetPassword(false)}>
                  Exit
              </button>
            </div>
          </form>
        </article>
      )}

      {isForgotPasswordSent && (
        <article className="login-form">
          <form onSubmit={handlePasswordResetSent}>
            <h2>Password Reset Link Sent!</h2>
            <p>You should receive a link to reset your password within 5 mins!</p>
            <div className='button-group'>
              <button className="login-button" type="submit">
                  Exit
              </button>
            </div>
          </form>
        </article>
      )}

      {isForgetPasswordFlow && !tokenIsValid && (
        <article className='login-form'>
          <h2>TOKEN NOT VALID</h2>
        </article>
      )}

      {isForgetPasswordFlow && tokenIsValid && !isLoggedIn && (
        <article className="login-form">
          <h2>Enter New Password</h2>
          <label>
            <input placeholder="New Password" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} />
          </label>
          <label>
            <input placeholder="Confirm New Password" type="password" value={password3} onChange={(e) => setPassword3(e.target.value)} />
          </label>
          <div className="button-group">
            <button className="login-button" type="button" onClick={() => handlePasswordReset(password2, password3)}>Reset Password</button>
          </div>
        </article>
      )}

      {passwordNotMatch && (
        <article className="login-form">
          <h2>Passwords Do Not Match</h2>
          <div className="button-group">
            <button className="login-button" type="button" onClick={() => setPasswordNotMatch(false)}>Exit</button>
          </div>
        </article>
      )}
    </div>
  );
};

export default DynamicComponentLoader;
